import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';

interface AuthContextType {
  idToken: string | null;
  refreshToken: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [idToken, setIdToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const refreshToken = async () => {
    try {
      const cachedToken = localStorage.getItem('idToken');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (cachedToken && tokenExpiry && new Date().getTime() < parseInt(tokenExpiry)) {
        setIdToken(cachedToken);
        return;
      }

      const session = await fetchAuthSession();
      if (session && session.tokens && session.tokens.idToken) {
        const newToken = session.tokens.idToken.toString();
        setIdToken(newToken);
        
        localStorage.setItem('idToken', newToken);
        localStorage.setItem('tokenExpiry', (new Date().getTime() + 55 * 60 * 1000).toString());
        
        // Store current user email in localStorage if available in the token
        try {
          // Access user claims from the token
          const payload = session.tokens.idToken.payload;
          if (payload && payload.email) {
            // Store user email in localStorage for use in other parts of the app
            const userInfo = {
              email: payload.email,
              name: payload.name || '',
              sub: payload.sub || ''
            };
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
            console.log("User email stored in localStorage:", payload.email);
          }
        } catch (error) {
          console.error("Failed to extract user email from token:", error);
        }
        
        console.log("ID Token refreshed and cached");
      } else {
        throw new Error("Failed to refresh token");
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      await handleSignOut();
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear all auth-related storage
      localStorage.removeItem('idToken');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('userInfo'); // Also clear userInfo
      sessionStorage.clear();
      
      // Show toast notification
      toast({
        title: "Signing out",
        description: "Please wait...",
        status: "info",
        duration: 2000,
        isClosable: false,
        position: "top"
      });

      // Sign out from Amplify
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIdToken(null);
      // Use a small delay to ensure the toast is visible
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1000);
    }
  };

  // Add this function to handle session timeouts

  useEffect(() => {
    refreshToken();
    const refreshInterval = setInterval(refreshToken, 54 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  return (
    <AuthContext.Provider value={{ idToken, refreshToken, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
